import React from "react";
import Header from "./header";
import Footer from "./footer";

const PrivacyPolicy = () => {
  return (
    <div className="bg-gray-50 text-gray-800 font-sans">
      {/* Header */}
      <div className="w-full shadow-md sticky top-0 z-50">
        <Header />
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-16">
        {/* Page Title */}
        <div className="text-center mb-5 mt-0">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-700">
            Privacy Policy
          </h1>
          <p className="mt-3 text-base md:text-lg text-slate-600 font-medium">
            Last updated:{" "}
            <span className="font-semibold text-slate-900">
              {new Date().toLocaleDateString("en-IN", {
                year: "numeric",
                month: "long",
                day: "numeric",
                timeZone: "Asia/Kolkata",
              })}
            </span>
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {/* INTRODUCTION */}
          <section className="bg-white rounded-2xl p-8 shadow-md border border-slate-200 hover:shadow-lg transition-all duration-300">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-5 border-l-4 border-blue-600 pl-3">
              INTRODUCTION
            </h2>
            <p className="text-slate-700 text-base leading-relaxed mb-4">
              This Privacy Policy describes how we collect, use, and protect your
              information when you use our Services. We are committed to
              safeguarding your privacy and ensuring compliance with Indian
              privacy laws including the Information Technology Act, 2000 and the
              Digital Personal Data Protection Act, 2023.
            </p>
          </section>

          {/* USER RIGHTS */}
          <section className="bg-white rounded-2xl p-8 shadow-md border border-slate-200 hover:shadow-lg transition-all duration-300">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-5 border-l-4 border-blue-600 pl-3">
              YOUR RIGHTS
            </h2>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              {/* Left Column */}
              <div className="space-y-3">
                <div className="flex items-start">
                  <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">
                      Access Rights
                    </h4>
                    <p className="text-gray-700 text-xs leading-4">
                      Request access to personal information we hold about you
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">
                      Correction Rights
                    </h4>
                    <p className="text-gray-700 text-xs leading-4">
                      Request correction of inaccurate or incomplete information
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">
                      Deletion Rights
                    </h4>
                    <p className="text-gray-700 text-xs leading-4">
                      Request deletion of personal information, subject to legal
                      requirements
                    </p>
                  </div>
                </div>
              </div>
              {/* Right Column */}
              <div className="space-y-3">
                <div className="flex items-start">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">
                      Consent Withdrawal
                    </h4>
                    <p className="text-gray-700 text-xs leading-4">
                      Withdraw consent for processing at any time
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">
                      Data Portability
                    </h4>
                    <p className="text-gray-700 text-xs leading-4">
                      Request personal data in structured, machine-readable
                      format
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">
                      Processing Restriction
                    </h4>
                    <p className="text-gray-700 text-xs leading-4">
                      Request restriction on processing of personal information
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* How to Exercise Rights */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-2">
                How to Exercise Your Rights
              </h4>
              <p className="text-gray-700 text-sm leading-5 mb-2">
                To exercise any of these rights, please contact our Data
                Protection Officer at{" "}
                <span className="text-blue-600 font-medium">
                  admin@anvitechsolutions.com
                </span>{" "}
                with:
              </p>
              <ul className="text-gray-700 text-sm space-y-1">
                <li>• Your full name and contact information</li>
                <li>• Specific details of your request</li>
                <li>• Proof of identity (as required by law)</li>
                <li>• Any relevant reference numbers or account details</li>
              </ul>
            </div>
          </section>

          {/* AUTOMATED DECISION MAKING */}
          <section className="bg-white rounded-2xl p-8 shadow-md border border-slate-200 hover:shadow-lg transition-all duration-300">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-5 border-l-4 border-blue-600 pl-3">
              Automated Decision Making and Chatbot Usage
            </h2>
            <p className="text-gray-700 leading-7 mb-4">
              Our website may use automated systems including chatbots to
              facilitate customer interactions and provide information. These
              systems:
            </p>
            <ul className="text-gray-700 leading-6 space-y-2 mb-4">
              <li>• Do not make automated decisions that significantly affect users</li>
              <li>• Collect minimal information necessary for customer service</li>
              <li>• Do not engage in profiling or behavioral tracking</li>
              <li>• Operate under human supervision for quality assurance</li>
            </ul>
            <p className="text-gray-700 leading-7">
              All chatbot interactions are logged for service improvement and are
              subject to the same privacy protections as other personal
              information.
            </p>
          </section>

          {/* GRIEVANCE REDRESSAL */}
          <section className="bg-white rounded-2xl p-8 shadow-md border border-slate-200 hover:shadow-lg transition-all duration-300">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-5 border-l-4 border-blue-600 pl-3">
              Grievance Redressal
            </h2>
            <div className="bg-indigo-50 p-6 rounded-lg">
              <p className="text-gray-700 leading-7 mb-4">
                In accordance with the Information Technology Act, 2000 and
                applicable rules, we have appointed a Grievance Officer to
                address privacy-related concerns:
              </p>
              <div className="space-y-2 text-gray-700">
                <p><strong>Grievance Officer:</strong> [Name]</p>
                <p>
                  <strong>Email:</strong>{" "}
                  <span className="text-blue-600">
                    admin@anvitechsolutions.com
                  </span>
                </p>
                <div>
                  <strong>Address:</strong>
                  <div className="ml-4 mt-1 text-sm leading-6">
                    <p>Anvitech Solutions Private Limited</p>
                    <p>Office No. C-403, Hetal Arch Premises</p>
                    <p>Malad West, Mumbai</p>
                    <p>Maharashtra - 400064, India</p>
                  </div>
                </div>
                <p>
                  <strong>Response Time:</strong> Within 30 days of receiving
                  complaint
                </p>
              </div>
            </div>
          </section>

          {/* MARKETING COMMUNICATIONS */}
          <section className="bg-white rounded-2xl p-8 shadow-md border border-slate-200 hover:shadow-lg transition-all duration-300">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-5 border-l-4 border-blue-600 pl-3">
              Marketing Communications and Opt-out
            </h2>
            <p className="text-gray-700 leading-7 mb-4">
              We send marketing communications only with your explicit consent.
              You may opt out at any time by:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">
                  Email Marketing
                </h4>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• Click unsubscribe link in emails</li>
                  <li>
                    • Email:{" "}
                    <span className="text-blue-600">
                      admin@anvitechsolutions.com
                    </span>
                  </li>
                  <li>• Update preferences in account settings</li>
                </ul>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">
                  Other Communications
                </h4>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>
                    • Contact:{" "}
                    <span className="text-blue-600">
                      admin@anvitechsolutions.com
                    </span>
                  </li>
                  <li>• Specify type of communication to opt out</li>
                  <li>• Administrative emails may continue as required</li>
                </ul>
              </div>
            </div>
          </section>

          {/* INTERNATIONAL DATA TRANSFERS */}
          <section className="bg-white rounded-2xl p-8 shadow-md border border-slate-200 hover:shadow-lg transition-all duration-300">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-5 border-l-4 border-blue-600 pl-3">
              International Data Transfers
            </h2>
            <p className="text-gray-700 leading-7">
              We primarily store and process data within India. Any international
              transfer of personal information will be conducted in compliance
              with applicable Indian laws and with appropriate safeguards to
              protect your privacy rights.
            </p>
          </section>

          {/* CONTACT INFORMATION */}
          <section className="bg-blue-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-300">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 border-l-4 border-blue-600 pl-3">
              Contact Information
            </h2>
            <p className="text-slate-700 leading-7 mb-3">
              If you have any questions about this Privacy Notice or our privacy
              practices, please contact us at:
            </p>
            <div className="text-slate-700 space-y-2">
              <p>
                <strong className="font-semibold text-slate-900">Email:</strong>{" "}
                <span className="text-blue-600">admin@anvitechsolutions.com</span>
              </p>
              <div>
                <strong className="font-semibold text-slate-900">Address:</strong>
                <div className="ml-4 mt-1 text-sm leading-6">
                  <p>Hetal Arch Premises, C-403</p>
                  <p>S.V. Road, Opp. Natraj Market</p>
                  <p>Malad West, Mumbai</p>
                  <p>Maharashtra - 400064, India</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <div className="w-full bg-slate-100 border-t">
        <Footer />
      </div>
    </div>
  );
};

export default PrivacyPolicy;
